import React, { useEffect, useState, useMemo } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { supabase } from '../../lib/supabase.js';
import { fetchMunicipiosByProvincia, fetchProvinciaIdByNombre } from '../../lib/ubicacionService.js';

const PLACEHOLDER = {
  provincia: 'Provincia',
  municipio: 'Municipio',
};

async function fetchProvinciasAll() {
  const { data, error } = await supabase.from('provincias').select('id_provincia,nombre').order('nombre', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

function makeSelectStyle(variant, disabled) {
  if (variant === 'light') {
    return {
      width: '100%',
      padding: '7px 9px',
      border: '1px solid rgba(0,0,0,0.14)',
      borderRadius: 6,
      fontSize: '0.79rem',
      outline: 'none',
      background: '#f8f6f2',
      color: '#1a1a1a',
      transition: 'border-color .18s',
      fontFamily: 'inherit',
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? 'not-allowed' : 'pointer',
    };
  }
  return {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '0.5px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.06)',
    color: '#fff',
    fontSize: 13,
    outline: 'none',
    fontFamily: 'inherit',
    opacity: disabled ? 0.45 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

/**
 * Selector encadenado Provincia → Municipio (datos Supabase / INE).
 * Valores controlados como **nombres** (TEXT), alineado con profiles/listings.ciudad(localidad).
 */
export default function UbicacionSelector({
  provincia = '',
  municipio = '',
  onProvinciaChange,
  onMunicipioChange,
  required = false,
  soloEspana = true,
  paisLibre = '',
  onPaisLibreChange,
  variant = 'dark',
  spacing = 10,
}) {
  const [provincias, setProvincias] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [loadPr, setLoadPr] = useState(true);
  const [loadMu, setLoadMu] = useState(false);
  const [err, setErr] = useState(null);

  const darkOpt = variant === 'dark';

  useEffect(() => {
    let c = true;
    setLoadPr(true);
    setErr(null);
    fetchProvinciasAll()
      .then(rows => {
        if (c) setProvincias(rows);
      })
      .catch(e => {
        if (c) setErr(e?.message || 'No se pudieron cargar provincias.');
      })
      .finally(() => {
        if (c) setLoadPr(false);
      });
    return () => {
      c = false;
    };
  }, []);

  useEffect(() => {
    let c = true;
    const provName = String(provincia || '').trim();
    if (!provName) {
      setMunicipios([]);
      return;
    }
    setLoadMu(true);
    setErr(null);
    (async () => {
      try {
        let id = null;
        const match = provincias.find(r => r.nombre === provName && r.id_provincia != null);
        if (match) id = match.id_provincia;
        else id = await fetchProvinciaIdByNombre(provName);
        if (!c) return;
        if (id == null) {
          setMunicipios([]);
          return;
        }
        const rows = await fetchMunicipiosByProvincia(id);
        if (!c) return;
        const m = String(municipio || '').trim();
        if (m && !rows.some(r => r.nombre === m)) {
          setMunicipios([{ id_municipio: null, nombre: m, _legacy: true }, ...rows]);
        } else {
          setMunicipios(rows);
        }
      } catch (e) {
        if (c) setErr(e?.message || 'No se pudieron cargar municipios.');
        if (c) setMunicipios([]);
      } finally {
        if (c) setLoadMu(false);
      }
    })();
    return () => {
      c = false;
    };
  }, [provincia, provincias, municipio]);

  const sel = useMemo(() => makeSelectStyle(variant, false), [variant]);
  const selDis = useMemo(() => makeSelectStyle(variant, true), [variant]);

  const onFocus = e => {
    e.target.style.borderColor = C.gold;
  };
  const onBlur = e => {
    e.target.style.borderColor = variant === 'light' ? 'rgba(0,0,0,0.14)' : 'rgba(255,255,255,0.12)';
  };

  const labelStyle =
    variant === 'light'
      ? { fontSize: '0.68rem', fontWeight: 700, color: '#aaa', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }
      : { fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 };

  const optBg = darkOpt ? { background: '#1a1d28', color: '#fff' } : { background: '#fff', color: '#1a1a1a' };

  const provListLoaded = provincias.length > 0 || !loadPr;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing }}>
      {!soloEspana && typeof onPaisLibreChange === 'function' && (
        <div>
          <div style={labelStyle}>
            País {required ? ' *' : ''}
          </div>
          <input
            value={paisLibre}
            onChange={e => onPaisLibreChange(e.target.value)}
            placeholder="País (texto libre)"
            style={
              variant === 'light'
                ? { ...sel, cursor: 'text' }
                : { ...sel, cursor: 'text' }
            }
            onFocus={onFocus}
            onBlur={onBlur}
          />
        </div>
      )}
      <div>
        <div style={labelStyle}>
          Provincia{required ? ' *' : ''}
        </div>
        <select
          value={
            provincias.some(p => p.nombre === provincia)
              ? provincia
              : provincia || ''
          }
          disabled={loadPr}
          onChange={e => {
            const v = e.target.value;
            onProvinciaChange?.(v);
            onMunicipioChange?.('');
          }}
          style={loadPr ? selDis : sel}
          onFocus={onFocus}
          onBlur={onBlur}
        >
          <option value="" style={optBg}>
            {loadPr ? 'Cargando…' : PLACEHOLDER.provincia}
          </option>
          {provincias.map(p => (
            <option key={`${p.id_provincia ?? 'x'}-${p.nombre}`} value={p.nombre} style={optBg}>
              {p.nombre}
            </option>
          ))}
          {provListLoaded &&
            String(provincia || '').trim() &&
            !provincias.some(p => p.nombre === provincia) && (
              <option key="legacy-prov" value={provincia} style={optBg}>
                {provincia} (guardado)
              </option>
            )}
        </select>
      </div>
      <div>
        <div style={labelStyle}>
          Municipio{required ? ' *' : ''}
        </div>
        <select
          value={municipios.some(m => m.nombre === municipio) ? municipio : municipio || ''}
          disabled={loadMu || !String(provincia || '').trim()}
          onChange={e => onMunicipioChange?.(e.target.value)}
          style={loadMu || !String(provincia || '').trim() ? selDis : sel}
          onFocus={onFocus}
          onBlur={onBlur}
        >
          <option value="" style={optBg}>
            {!String(provincia || '').trim() ? `— Primero ${PLACEHOLDER.provincia.toLowerCase()} —` : loadMu ? 'Cargando…' : PLACEHOLDER.municipio}
          </option>
          {municipios.map(m => (
            <option key={`${m.id_municipio ?? 'x'}-${m.nombre}`} value={m.nombre} style={optBg}>
              {m.nombre}
              {m._legacy ? ' (guardado)' : ''}
            </option>
          ))}
        </select>
      </div>
      {err && (
        <div style={{ fontSize: 11, color: variant === 'light' ? '#b91c1c' : '#f87171' }}>{err}</div>
      )}
    </div>
  );
}
